import React from "react";

interface LogoProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
  loading?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  variant?: "default" | "gradient" | "pulse";
}

const Logo: React.FC<LogoProps> = ({
  className,
  loading = false,
  size = "md",
  variant = "default",
  ...props
}) => {
  const sizeClasses = {
    sm: "h-8 w-auto",
    md: "h-12 w-auto",
    lg: "h-16 w-auto",
    xl: "h-24 w-auto",
  };

  const getWrapperClasses = () => {
    let classes = "logo-hover";
    if (loading) classes += " logo-loading";
    if (variant === "gradient") classes += " logo-gradient";
    if (variant === "pulse") classes += " logo-pulse";
    return classes;
  };

  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0"
      y="0"
      viewBox="0 140 550 200"
      xmlSpace="preserve"
      className={`${sizeClasses[size]} ${getWrapperClasses()} ${className ?? ""}`}
      {...props}
    >
      <style>
        {`
          .st1, .txt_land, .top_stripe { fill: var(--usland-blue); }
          .txt_us, .bottom_stripe { fill: var(--usland-red); }

          /* Loading Animation Styles */
          .logo-loading .top_stripe {
            animation: arcFlow 2.5s ease-in-out infinite;
            transform-origin: center;
          }
          .logo-loading .bottom_stripe {
            animation: arcFlow 2.5s ease-in-out infinite;
            animation-delay: 0.4s;
            transform-origin: center;
          }
          .logo-loading .txt_us {
            animation: letterPulse 3s ease-in-out infinite;
          }
          .logo-loading .txt_land {
            animation: letterPulse 3s ease-in-out infinite;
            animation-delay: 0.2s;
          }
          
          /* Hover Animation Styles */
          .logo-hover:hover .top_stripe {
            animation: arcHover 1.5s ease-in-out;
          }
          .logo-hover:hover .bottom_stripe {
            animation: arcHover 1.5s ease-in-out;
            animation-delay: 0.15s;
          }
          .logo-hover:hover .txt_us,
          .logo-hover:hover .txt_land {
            animation: letterGlow 1.5s ease-in-out;
          }
          
          /* Cool gradient effect for special states */
          .logo-gradient .top_stripe {
            fill: url(#gradientTop);
          }
          .logo-gradient .bottom_stripe {
            fill: url(#gradientBottom);
          }
          .logo-gradient .txt_us {
            fill: url(#gradientUS);
          }
          .logo-gradient .txt_land {
            fill: url(#gradientLAND);
          }
          
          @keyframes arcFlow {
            0% { 
              opacity: 0.6;
              transform: translateX(-15px) scale(0.95);
            }
            25% {
              opacity: 1;
              transform: translateX(-5px) scale(1.02);
            }
            50% { 
              opacity: 0.8;
              transform: translateX(0px) scale(1);
            }
            75% {
              opacity: 1;
              transform: translateX(5px) scale(1.02);
            }
            100% { 
              opacity: 0.6;
              transform: translateX(15px) scale(0.95);
            }
          }
          
          @keyframes arcHover {
            0% { 
              transform: scale(1) rotateZ(0deg);
            }
            30% {
              transform: scale(1.05) rotateZ(2deg);
            }
            60% {
              transform: scale(1.02) rotateZ(-1deg);
            }
            100% { 
              transform: scale(1) rotateZ(0deg);
            }
          }
          
          @keyframes letterPulse {
            0%, 100% { 
              opacity: 1;
              transform: scale(1);
            }
            50% { 
              opacity: 0.7;
              transform: scale(1.02);
            }
          }
          
          @keyframes letterGlow {
            0%, 100% { 
              filter: brightness(1) drop-shadow(0 0 0px transparent);
            }
            50% { 
              filter: brightness(1.2) drop-shadow(0 0 8px currentColor);
            }
          }
          
          /* Pulse effect for special occasions */
          .logo-pulse {
            animation: logoPulse 2s ease-in-out infinite;
          }
          
          @keyframes logoPulse {
            0%, 100% { 
              transform: scale(1);
              filter: brightness(1);
            }
            50% { 
              transform: scale(1.05);
              filter: brightness(1.1);
            }
          }
        `}
      </style>
      {/* Gradient Definitions for Special Effects */}
      <defs>
        <linearGradient id="gradientTop" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--us-land-blue))" />
          <stop offset="50%" stopColor="hsl(220, 70%, 60%)" />
          <stop offset="100%" stopColor="hsl(200, 80%, 50%)" />
        </linearGradient>
        <linearGradient id="gradientBottom" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--us-land-red))" />
          <stop offset="50%" stopColor="hsl(340, 70%, 60%)" />
          <stop offset="100%" stopColor="hsl(320, 80%, 50%)" />
        </linearGradient>
        <linearGradient id="gradientUS" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--us-land-red))" />
          <stop offset="100%" stopColor="hsl(340, 80%, 60%)" />
        </linearGradient>
        <linearGradient id="gradientLAND" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--us-land-blue))" />
          <stop offset="100%" stopColor="hsl(200, 80%, 60%)" />
        </linearGradient>
      </defs>
      <g>
        <g>
          <path
            className="txt_us letter_s"
            d="M290.04,269.76H240.2c0,0-4.63,0-4.63,2.72c0,1.85,0,1.36,0,3.18c0,2,0.04,2.32,4.63,2.32
            c10.85,0,26.21,0,38.26,0c12.05,0,11.59,10.77,11.59,10.77s0.08-1.31,0.08,10.48s-11.67,10.92-11.67,10.92l-54.37-0.08
            l-0.03-10.86l50.01,0c0,0,4.7-0.29,4.7-3.64c0,0,0-1.38,0-3.52c0-2.93-4.7-3.29-4.7-3.29s-26.27,0-38.49,0
            s-11.28-10.77-11.28-10.77s0,0.84,0-8.22c0-10.69,11.28-10.69,11.28-10.69l54.56,0.22L290.04,269.76z"
          />
          <path
            className="txt_us letter_u"
            d="M147.52,259.07h10.97c0,0,0,18.92,0,29.69c0,10.43,0,10.51,10.32,10.51c1.96,0,30.93,0,30.93,0v-40.2h11.04
            v51.08h-52.3c0,0-10.97,2.7-10.97-10.88C147.52,285.68,147.52,259.07,147.52,259.07z"
          />
        </g>
        <path
          className="txt_land letter_n"
          d="M420.44,258.97l6.38,0.05c0,0,1.84-0.7,3.87,1.9c2.04,2.6,24.53,29.8,24.53,29.8l-0.03-30.23
          c0,0,0-1.58,2.82-1.53c6.07,0.11,3.82,0.11,6.13,0.11c2.28,0,2.28,0,2.28,3.16v5.55v40.67c0,0,0,1.7-1.34,1.7
          c-2.07,0-1.19,0-7.79,0c-3.2,0-3.21-1.44-6.46-5.36c-20.88-24.84-20.89-24.94-20.89-24.94l-0.09,28.13c0,0,0.09,2.17-1.85,2.11
          c-1.99-0.06-7.36-0.05-7.36-0.05s-1.75,0.36-1.78-2.32c-0.03-2.68,0.07-45.81,0.07-45.81S418.65,258.96,420.44,258.97z"
        />
        <path
          className="txt_land letter_d"
          d="M492.21,269.76h19.41c0,0,6.77,0,6.77,6.93c0,7.21,0,16.15,0,16.15s0,6.43-6.86,6.43c-8.01,0-19.32,0-19.32,0
          V269.76z M482.14,258.98c-0.42-0.06-1.27,0.25-1.27,3.77c0,7.01-0.06,39.99-0.06,43.92s1.9,3.5,1.9,3.5l30.28-0.16
          c0,0,16.6,0.13,16.6-13.52c0-9.68-0.13-21.6-0.13-26.5c0-4.9-1.59-10.97-16.69-10.93c-11.68-0.04-30.4,0-30.4,0
          S482.28,259,482.14,258.98z"
        />
        <path
          className="txt_land letter_l"
          d="M307.19,259.07c2.28,0,2.28,0.55,2.28,1.75c0,10.05-0.03,38.39-0.03,38.39s23.87,0.05,30.44,0.05
          c2.62,0,2.62,0,2.62,2.38c0,3.66,0,3.81,0,6.81c0,1.69,0,1.81-3.74,1.78c-11.45-0.09-29.29-0.09-37.72-0.16
          c-2.76-0.02-2.76,0.16-2.76-2.87c0-16.49,0-39.38,0-46.38c0-1.23,0-1.75,2.5-1.75"
        />
        <path
          className="txt_land letter_a"
          d="M379.25,272.43l7,16.32h-14.23L379.25,272.43z M383.26,259.07c-3.35,0-7.26,0-7.26,0c-4.49,0-4.17,0-7.24,6.62
          c-16.34,34.45-0.01,0-19.94,41.74c-1.28,2.68-1.32,2.72,1.15,2.72c2.81,0,6.16,0,9.98,0c2.62,0,2.48,0.6,4.29-3.55
          c3.19-7.34,3.16-7.34,3.16-7.34h23.27c0,0,1.06,2.18,3.27,7.34c1.45,3.37,1.73,3.55,3.73,3.55c3.16,0,5.92,0,10.56,0
          c3.37,0,1.95,0,0.12-4.67c-2.12-4.33-6.43-13.31-6.43-13.31s-0.14,0.25-12.53-26.91C386.57,259.07,387.14,259.07,383.26,259.07z"
        />
        <path
          className="top_stripe"
          d="M529.59,251.1c-67.49,0-67.49,0-67.49,0c-93.15-144.32-314.4,0.09-314.4,0.09
          C271.88,123.95,502.63,141.52,529.59,251.1z"
        />
        <path
          className="bottom_stripe"
          d="M416.92,251.19c-64.48,0-64.6,0-64.6,0c-33.69-75.64-204.62,0-204.62,0
          C236.73,183.23,381.39,162.77,416.92,251.19z"
        />
      </g>
    </svg>
  );
};

export default Logo;
